import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';

import { selectDocumentTypes } from '../../../store/dataSelectors';
import {
    loadDocumentTypesForContractId,
    loadParticipantStatusDetails,
    loadParticipantStatusReasonDetails
} from '../../../store/directusService';
import { loadContractConstants, updateContractConstants } from '../../../store/participantService';
import { getUniqueElements } from '../../../utils/arrayUtils';
import { regExps } from '../../../utils/formValidation/validationPatterns';
import { clearKeys } from '../../../utils/objectUtils';
import { hasRole, SUPERUSER } from '../../../utils/userRoles';
import Button from '../../formElements/Button';
import MultiSelect from '../../formElements/MultiSelect';
import RadioButtons from '../../formElements/RadioButtons';
import SingleSelect from '../../formElements/SingleSelect';
import Switch from '../../formElements/Switch';
import TextInputField from '../../formElements/TextInputField';
import AccordionHeader from '../../ui/accordion/AccordionHeader';
import AccordionIcon from '../../ui/accordion/AccordionIcon';
import LoadingSpinner from '../../ui/LoadingSpinner';

import EditParticipantStatuses from './EditParticipantStatuses';
import { validate } from './validateParticipantStatusAdmin';

import '../../../commonStyles/loading.css';
import form from '../../../commonStyles/formStyles.module.css';
import local from './participantStatusAdminStyles.module.css';

const ParticipantStatusAdmin = ({ contractId }) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const initialState = {
        id: '',
        contractId: '',
        activeStatusDocumentTypeIds: [],
        registeredStatusDocumentTypeIds: [],
        employedStatusDocumentTypeIds: [],
        registeredStatusReasonId: '',
        allowFirstCourseModuleToSetActive: false,
        allowCourseSubmissionOrDigitalToSetCourseBooked: false,
        daysWithoutInitialAppointmentUntilRecordClosure: '',
        setNoInitialClosureReason: '',
        requirePoNumber: true,
        deleteParticipantPiOnContractClosure: false,
        deleteParticipantPiOnContractClosureDays: null,
        deleteParticipantPiOnParticipantClosure: false,
        deleteParticipantPiOnParticipantClosureDays: null,
        uniqueIdRequiredOnCreation: true,
        documentTypes: [],
        closedAfter3XMissedCourseModules: false,
        participantStatuses: [],
        initialAppointmentClosureDaysCalculationType: true,
        configureOnHoldStatus: false,
        daysInOnHoldStatus: ''
    };

    const [newEntry, setNewEntry] = useState(initialState);
    const [initialEntryState, setInitialEntryState] = useState(initialState);
    const [errors, setErrors] = useState({});
    const [preSelectedActiveDocumentTypes, setPreSelectedActiveDocumentTypes] = useState([]);
    const [preSelectedActiveDocumentTypeIds, setPreSelectedActiveDocumentTypeIds] = useState([]);
    const [preSelectedRegisteredDocumentTypes, setPreSelectedRegisteredDocumentTypes] = useState(
        []
    );
    const [preSelectedRegisteredDocumentTypeIds, setPreSelectedRegisteredDocumentTypeIds] =
        useState([]);
    const [preSelectedEmployedStatusDocumentTypes, setPreSelectedEmployedStatusDocumentTypes] =
        useState([]);
    const [preSelectedEmployedStatusDocumentTypeIds, setPreSelectedEmployedStatusDocumentTypeIds] =
        useState([]);
    const [selectedStatuses, setSelectedStatuses] = useState([]);
    const [selectedStatusesIds, setSelectedStatusesIds] = useState([]);
    const [keys, setKeys] = useState({
        activeStatusDocumentTypeIds: '0',
        setNoInitialClosureReason: '1',
        registeredStatusDocumentTypeIds: '2',
        registeredStatusReasonId: '3',
        employedStatusDocumentTypeIds: '4'
    });
    const [statusAccordionState, setStatusAccordionState] = useState({});
    const [toStatuses, setToStatuses] = useState([]);
    const [lockedStatuses, setLockedStatuses] = useState([]);
    const [isDisabled, setIsDisabled] = useState(true);
    const [isUpdateButtonDisabled, setIsUpdateButtonDisabled] = useState(false);

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const contractConstants = useSelector(
        (state) => state.entities.participantService.contractConstants
    );
    const participantStatusDetails = useSelector(
        (state) => state.entities.directusService.participantStatusDetails
    );
    const participantStatusReasonDetails = useSelector(
        (state) => state.entities.directusService.participantStatusReasonDetails
    );
    const documentTypes = useSelector(selectDocumentTypes);
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);
    const errorMessage = useSelector((state) => state.entities.formsState.errorMessage);

    // USE EFFECTS
    useEffect(() => {
        if (participantStatusReasonDetails?.length < 1)
            dispatch(loadParticipantStatusReasonDetails());
    }, []);

    useEffect(() => {
        setIsDisabled(!hasRole([SUPERUSER], roles));
    }, [roles]);

    useEffect(() => {
        if (contractId) contractSetup();
    }, [contractId]);

    useEffect(() => {
        if (documentTypes.length < 1 || Object.keys(contractConstants).length < 1) return;
        const activeDocTypeIds = contractConstants?.activeStatusDocumentTypeIds || [];
        const activeDocTypes =
            activeDocTypeIds.length > 0
                ? activeDocTypeIds
                      .map((el) => documentTypes.find((entry) => entry.id === el))
                      .filter((el) => el)
                : [];
        const registeredDocTypeIds = contractConstants?.registeredStatusDocumentTypeIds || [];
        const registeredDocTypes =
            registeredDocTypeIds.length > 0
                ? registeredDocTypeIds
                      .map((el) => documentTypes.find((entry) => entry.id === el))
                      .filter((el) => el)
                : [];
        const employedStatusDocTypeIds = contractConstants?.employedStatusDocumentTypeIds || [];
        const employedStatusDocTypes =
            employedStatusDocTypeIds.length > 0
                ? employedStatusDocTypeIds
                      .map((el) => documentTypes.find((entry) => entry.id === el))
                      .filter((el) => el)
                : [];
        setPreSelectedActiveDocumentTypeIds(activeDocTypeIds);
        setPreSelectedActiveDocumentTypes(activeDocTypes);
        setPreSelectedRegisteredDocumentTypeIds(registeredDocTypeIds);
        setPreSelectedRegisteredDocumentTypes(registeredDocTypes);
        setPreSelectedEmployedStatusDocumentTypeIds(employedStatusDocTypeIds);
        setPreSelectedEmployedStatusDocumentTypes(employedStatusDocTypes);
        const newData = {
            ...contractConstants,
            requirePoNumber:
                contractConstants.requirePoNumber === null
                    ? true
                    : contractConstants.requirePoNumber,
            uniqueIdRequiredOnCreation:
                contractConstants.uniqueIdRequiredOnCreation === null
                    ? true
                    : contractConstants.uniqueIdRequiredOnCreation,
            allowFirstCourseModuleToSetActive:
                contractConstants.allowFirstCourseModuleToSetActive ?? false,
            allowCourseSubmissionOrDigitalToSetCourseBooked:
                contractConstants.allowCourseSubmissionOrDigitalToSetCourseBooked ?? false,
            closedAfter3XMissedCourseModules:
                contractConstants.closedAfter3XMissedCourseModules ?? false,
            participantStatuses: contractConstants.participantStatuses,
            initialAppointmentClosureDaysCalculationType:
                contractConstants.initialAppointmentClosureDaysCalculationType !== 'Working Days'
        };
        setNewEntry(newData);
        setInitialEntryState(contractConstants);
        setLockedStatuses(
            contractConstants.participantStatuses.map((el) => el.participantStatusId)
        );
    }, [documentTypes, contractConstants]);

    useEffect(() => {
        if (!('participantStatuses' in contractConstants)) return;
        setSelectedStatusesIds(
            contractConstants.participantStatuses.map((el) => el.participantStatusId)
        );
        const newSelectedStatuses = contractConstants.participantStatuses.map((el) => ({
            id: el.participantStatusId,
            name: el.participantStatus
        }));
        setToStatuses(newSelectedStatuses);
        setSelectedStatuses(newSelectedStatuses);
    }, [participantStatusDetails, contractConstants.participantStatuses, contractId]);

    useEffect(() => {
        const hasErrors = Object.values(errors).some((el) => el.error);
        setIsUpdateButtonDisabled(hasErrors);
        if (!hasErrors && Object.keys(errors).length) setErrors({});
    }, [errors]);

    useEffect(() => {
        if (successMessage === 'Contract constants has been updated' || errorMessage)
            setIsUpdateButtonDisabled(false);
    }, [successMessage, errorMessage]);

    // OTHER FNS
    const clearValues = () => {
        setNewEntry(initialState);
        setKeys(clearKeys(keys));
        setPreSelectedActiveDocumentTypeIds([]);
        setPreSelectedActiveDocumentTypes([]);
        setPreSelectedRegisteredDocumentTypeIds([]);
        setPreSelectedRegisteredDocumentTypes([]);
        setSelectedStatusesIds([]);
        setSelectedStatuses([]);
        setToStatuses([]);
        setLockedStatuses([]);
        setErrors({});
    };

    const contractSetup = () => {
        clearValues();
        if (
            Object.keys(contractConstants)?.length < 1 ||
            ('contractId' in contractConstants && contractConstants.contractId !== contractId)
        )
            dispatch(loadContractConstants(contractId));
        dispatch(loadDocumentTypesForContractId(contractId));
        participantStatusDetails?.length < 1 && dispatch(loadParticipantStatusDetails());
    };

    const clearError = (key) => {
        key in errors &&
            setErrors((errors) => ({ ...errors, [key]: { error: false, message: '' } }));
    };

    // EVENT HANDLERS
    const onUpdate = (key, value) => {
        clearError(key);
        setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    const onUpdateConfigureOnHoldStatus = (option) => {
        if (!option) onUpdate('daysInOnHoldStatus', '');
        onUpdate('configureOnHoldStatus', option);
    };

    const onStatusesChanged = (chosenIds) => {
        if (participantStatusDetails?.length < 1) return;
        clearError('participantStatuses');
        const selectedStatusIds = newEntry.participantStatuses.map((el) => el.participantStatusId);
        if (getUniqueElements(chosenIds, selectedStatusIds).length === 0) return;
        const idsToRemove = selectedStatusIds.filter((el) => !chosenIds.includes(el));
        const idsToAdd = chosenIds.filter((el) => !selectedStatusIds.includes(el));

        let updatedParticipantStatuses = newEntry.participantStatuses;
        if (idsToRemove.length > 0) {
            if (Object.keys(errors).length > 0) {
                const existingErrorsToRemove = idsToRemove
                    .map((el) => Object.keys(errors).filter((entry) => entry.includes(el)))
                    .flat();
                if (existingErrorsToRemove.length) {
                    existingErrorsToRemove.forEach((el) => clearError(el));
                }
            }
            updatedParticipantStatuses = [
                ...newEntry.participantStatuses
                    .filter((el) => !idsToRemove.includes(el.participantStatusId))
                    .map((el) => ({
                        ...el,
                        toParticipantStatuses: el.toParticipantStatuses.filter(
                            (entry) => !idsToRemove.includes(entry.participantStatusId)
                        )
                    }))
            ];
        }
        if (idsToAdd.length > 0) {
            updatedParticipantStatuses = [
                ...newEntry.participantStatuses,
                ...idsToAdd.map((el) => ({
                    participantStatusId: el,
                    participantStatus: participantStatusDetails.find((entry) => entry.id === el)
                        .name,
                    active: true,
                    toParticipantStatuses: []
                }))
            ];
        }
        setToStatuses(
            chosenIds.map((el) => participantStatusDetails.find((entry) => entry.id === el))
        );
        setNewEntry((prev) => ({
            ...prev,
            participantStatuses: updatedParticipantStatuses
        }));
    };

    const onStatusChanged = (participantStatus) => {
        setNewEntry((prev) => ({
            ...prev,
            participantStatuses: newEntry.participantStatuses.map((el) =>
                el.participantStatusId === participantStatus.participantStatusId
                    ? participantStatus
                    : el
            )
        }));
    };

    const onUpdateMultipleFields = (entries) => {
        Object.keys(entries).forEach((el) => clearError(el));
        setNewEntry({ ...newEntry, ...entries });
    };

    const onUpdateParticipantClosure = (option) => {
        if (option) onUpdate('deleteParticipantPiOnParticipantClosure', option);
        else {
            onUpdateMultipleFields({
                deleteParticipantPiOnParticipantClosureDays: null,
                deleteParticipantPiOnParticipantClosure: option
            });
        }
    };

    const onUpdateContractClosure = (option) => {
        if (option) onUpdate('deleteParticipantPiOnContractClosure', option);
        else {
            onUpdateMultipleFields({
                deleteParticipantPiOnContractClosureDays: null,
                deleteParticipantPiOnContractClosure: option
            });
        }
    };

    const onSubmit = (e) => {
        e.preventDefault();
        setIsUpdateButtonDisabled(true);
        const payload = {
            ...newEntry,
            initialAppointmentClosureDaysCalculationType:
                newEntry.initialAppointmentClosureDaysCalculationType === true
                    ? 'Calendar Days'
                    : 'Working Days',
            contractId
        };
        const newErrors = validate(payload, initialEntryState);
        if (Object.values(newErrors).some((el) => el.error)) {
            setErrors(newErrors);
            return;
        }
        dispatch(updateContractConstants(payload));
    };

    // RENDER
    const participantStatusesAccordions = () => {
        return newEntry.participantStatuses.map((el) => {
            const statusDetails = participantStatusDetails.find(
                (entry) => entry.id === el.participantStatusId
            );
            if (!statusDetails) return;
            const accordionContent = [
                <AccordionSummary
                    key={`accordionSummary${statusDetails.id}`}
                    expandIcon={<AccordionIcon />}
                    aria-controls="panel3b-content"
                    id="panel3b-header">
                    <AccordionHeader isValid={!errors[`accordion-${statusDetails.id}`]?.error}>
                        Participant Status - {statusDetails.name}
                    </AccordionHeader>
                    <Switch
                        id={`isParticipantStatusActive-${statusDetails.id}`}
                        checked={el.active}
                        inline={true}
                        onOff={true}
                        onChange={(e) => {
                            onStatusChanged({
                                ...el,
                                active: e.target.checked
                            });
                        }}
                    />
                </AccordionSummary>,
                <AccordionDetails key={`accordionDetails${statusDetails.id}`}>
                    <EditParticipantStatuses
                        key={statusDetails.id}
                        status={{ ...el }}
                        toStatuses={toStatuses}
                        onStatusChanged={onStatusChanged}
                        participantStatusReasonDetails={participantStatusReasonDetails}
                        errors={errors}
                        clearError={clearError}
                    />
                </AccordionDetails>
            ];
            if (
                errors?.participantStatuses &&
                errors.participantStatuses[statusDetails.id]?.error === false
            ) {
                if (!Object.hasOwn(statusAccordionState, el.participantStatusId))
                    setStatusAccordionState({
                        ...statusAccordionState,
                        [el.participantStatusId]: true
                    });
                return (
                    <Accordion
                        key={`accordionControlled${el.participantStatusId}`}
                        slotProps={{ transition: { mountOnEnter: true, unmountOnExit: true } }}
                        expanded={true}>
                        {accordionContent}
                    </Accordion>
                );
            }
            return (
                <Accordion
                    key={`accordionUncontrolled${el.participantStatusId}`}
                    slotProps={{ transition: { mountOnEnter: true, unmountOnExit: true } }}
                    defaultExpanded={!!statusAccordionState[el.participantStatusId]}>
                    {accordionContent}
                </Accordion>
            );
        });
    };

    if (!contractId) return <LoadingSpinner content={'No Contract Id'} time={0} />;

    return (
        <div>
            <form className={form.formWrapper} onSubmit={onSubmit}>
                <div className={form.formSection}>
                    <div className={form.formColumn}>
                        <MultiSelect
                            id="activeStatusDocumentTypeIds"
                            key={keys.activeStatusDocumentTypeIds}
                            label="Set Active Status Document Types"
                            placeholder="Document types"
                            disabled={isDisabled}
                            menuItems={documentTypes || []}
                            preSelectedIds={preSelectedActiveDocumentTypeIds}
                            preSelects={preSelectedActiveDocumentTypes}
                            onChange={(chosenIds) =>
                                onUpdate('activeStatusDocumentTypeIds', chosenIds)
                            }
                        />
                        <RadioButtons
                            id="allowFirstCourseModuleToSetActive"
                            label="Allow First Course Module To Set Active"
                            disabled={isDisabled}
                            mandatory={true}
                            value={newEntry.allowFirstCourseModuleToSetActive}
                            onChange={(option) =>
                                onUpdate('allowFirstCourseModuleToSetActive', option)
                            }
                        />
                        <RadioButtons
                            id="allowCourseSubmissionOrDigitalToSetCourseBooked"
                            label="Allow Course Submission Or Digital To Set Course Booked"
                            disabled={isDisabled}
                            mandatory={true}
                            value={newEntry.allowCourseSubmissionOrDigitalToSetCourseBooked}
                            onChange={(option) =>
                                onUpdate('allowCourseSubmissionOrDigitalToSetCourseBooked', option)
                            }
                        />
                        <TextInputField
                            id={'daysWithoutInitialAppointmentUntilRecordClosure'}
                            label={'Days without Initial Appointment until Record Closure'}
                            placeholder={'Enter number of days'}
                            mandatory={true}
                            maxLength={3}
                            disabled={isDisabled}
                            value={newEntry.daysWithoutInitialAppointmentUntilRecordClosure || ''}
                            error={errors.daysWithoutInitialAppointmentUntilRecordClosure}
                            onChange={(e) =>
                                onUpdate(
                                    'daysWithoutInitialAppointmentUntilRecordClosure',
                                    e.target.value.replace(/[^\d]/g, '')
                                )
                            }
                        />
                        <Switch
                            id={'initialAppointmentClosureDaysCalculationType'}
                            label="Days Calculated From Working Days or Calendar Days"
                            leftLabel="Working Days"
                            rightLabel="Calendar Days"
                            disabled={isDisabled}
                            checked={newEntry.initialAppointmentClosureDaysCalculationType}
                            onChange={(e) =>
                                onUpdate(
                                    'initialAppointmentClosureDaysCalculationType',
                                    e.target.checked
                                )
                            }
                        />
                        <SingleSelect
                            id={'setNoInitialClosureReason'}
                            key={keys.setNoInitialClosureReason}
                            label={'Set No Initial Closure Reason'}
                            placeholder="Search no initial closure reasons..."
                            disabled={isDisabled}
                            menuItems={participantStatusReasonDetails || []}
                            selectedId={newEntry.setNoInitialClosureReason || ''}
                            selected={
                                participantStatusReasonDetails.find(
                                    (el) => el.id === newEntry.setNoInitialClosureReason
                                ) || {}
                            }
                            onChange={(chosenId) => onUpdate('setNoInitialClosureReason', chosenId)}
                        />
                        <MultiSelect
                            id="participantStatuses"
                            label="Participant Statuses"
                            placeholder="Participant statuses"
                            mandatory={true}
                            disabled={isDisabled}
                            nonEditableIds={lockedStatuses}
                            menuItems={participantStatusDetails || []}
                            preSelectedIds={selectedStatusesIds}
                            preSelects={selectedStatuses}
                            error={errors.participantStatuses}
                            onChange={onStatusesChanged}
                        />

                        <RadioButtons
                            id="configureOnHoldStatus"
                            label="Configure On Hold Status?"
                            disabled={isDisabled}
                            value={newEntry.configureOnHoldStatus || false}
                            onChange={onUpdateConfigureOnHoldStatus}
                        />
                        <TextInputField
                            id="daysInOnHoldStatus"
                            label="How many Days in Status?"
                            placeholder={'Enter number of days in status...'}
                            mandatory={newEntry.configureOnHoldStatus}
                            disabled={!newEntry.configureOnHoldStatus || isDisabled}
                            maxLength={3}
                            value={newEntry.daysInOnHoldStatus || ''}
                            error={errors.daysInOnHoldStatus}
                            onChange={(e) =>
                                onUpdate(
                                    'daysInOnHoldStatus',
                                    e.target.value.replace(regExps.DIGITS_R, '')
                                )
                            }
                        />
                    </div>
                    <div className={form.formColumn}>
                        <MultiSelect
                            id="registeredStatusDocumentTypeIds"
                            key={keys.registeredStatusDocumentTypeIds}
                            label="Set Registered Status Document Types"
                            placeholder="Registered status document types"
                            disabled={isDisabled}
                            menuItems={documentTypes || []}
                            preSelectedIds={preSelectedRegisteredDocumentTypeIds}
                            preSelects={preSelectedRegisteredDocumentTypes}
                            onChange={(chosenIds) =>
                                onUpdate('registeredStatusDocumentTypeIds', chosenIds)
                            }
                        />
                        <SingleSelect
                            id="registeredStatusReasonId"
                            key={keys.registeredStatusReasonId}
                            label="Set Registered Status Reason"
                            placeholder="Search registered status reasons..."
                            disabled={isDisabled}
                            menuItems={participantStatusReasonDetails || []}
                            selectedId={newEntry.registeredStatusReasonId || ''}
                            selected={
                                participantStatusReasonDetails.find(
                                    (el) => el.id === newEntry.registeredStatusReasonId
                                ) || {}
                            }
                            onChange={(chosenId) => onUpdate('registeredStatusReasonId', chosenId)}
                        />
                        <RadioButtons
                            id="requirePoNumber"
                            label="Requires PO Number to be set to Registered"
                            mandatory={true}
                            disabled={isDisabled}
                            value={newEntry.requirePoNumber}
                            onChange={(option) => onUpdate('requirePoNumber', option)}
                        />
                        <MultiSelect
                            id="employedStatusDocumentTypeIds"
                            key={keys.employedStatusDocumentTypeIds}
                            label="Set Employed Status Document Types"
                            placeholder="Set employed status document types..."
                            disabled={isDisabled}
                            menuItems={documentTypes || []}
                            preSelectedIds={preSelectedEmployedStatusDocumentTypeIds}
                            preSelects={preSelectedEmployedStatusDocumentTypes}
                            onChange={(chosenIds) =>
                                onUpdate('employedStatusDocumentTypeIds', chosenIds)
                            }
                        />
                        <RadioButtons
                            id="deleteParticipantPiOnParticipantClosure"
                            label="Delete Participant PI on Participant Closure"
                            disabled={isDisabled}
                            value={!!newEntry.deleteParticipantPiOnParticipantClosure}
                            onChange={onUpdateParticipantClosure}
                        />
                        <TextInputField
                            id="deleteParticipantPiOnParticipantClosureDays"
                            label={"If 'Yes', how many Days after?"}
                            placeholder={'Enter number of days'}
                            mandatory={newEntry.deleteParticipantPiOnParticipantClosure}
                            disabled={
                                !newEntry.deleteParticipantPiOnParticipantClosure || isDisabled
                            }
                            maxLength={3}
                            value={newEntry.deleteParticipantPiOnParticipantClosureDays || ''}
                            error={errors.deleteParticipantPiOnParticipantClosureDays}
                            onChange={(e) =>
                                onUpdate(
                                    'deleteParticipantPiOnParticipantClosureDays',
                                    e.target.value.replace(/[^\d]/g, '')
                                )
                            }
                        />
                        <RadioButtons
                            id="deleteParticipantPiOnContractClosure"
                            label="Delete Participant PI on Contract Closure"
                            disabled={isDisabled}
                            value={!!newEntry.deleteParticipantPiOnContractClosure}
                            onChange={onUpdateContractClosure}
                        />
                        <TextInputField
                            id="deleteParticipantPiOnContractClosureDays"
                            label={"If 'Yes', how many Days after?"}
                            placeholder={'Enter number of days'}
                            mandatory={newEntry.deleteParticipantPiOnContractClosure}
                            disabled={!newEntry.deleteParticipantPiOnContractClosure || isDisabled}
                            maxLength={3}
                            value={newEntry.deleteParticipantPiOnContractClosureDays || ''}
                            error={errors.deleteParticipantPiOnContractClosureDays}
                            onChange={(e) =>
                                onUpdate(
                                    'deleteParticipantPiOnContractClosureDays',
                                    e.target.value.replace(/[^\d]/g, '')
                                )
                            }
                        />
                        <RadioButtons
                            id="uniqueIdRequiredOnCreation"
                            label="Unique ID Required on Creation?"
                            mandatory={true}
                            disabled={isDisabled}
                            value={newEntry.uniqueIdRequiredOnCreation}
                            onChange={(option) => onUpdate('uniqueIdRequiredOnCreation', option)}
                        />
                        <RadioButtons
                            id="closedAfter3XMissedCourseModules"
                            label="Set Closed After 3x Missed Course Modules"
                            disabled={isDisabled}
                            value={newEntry.closedAfter3XMissedCourseModules}
                            onChange={(option) =>
                                onUpdate('closedAfter3XMissedCourseModules', option)
                            }
                        />
                    </div>
                </div>
                <div className={local.accordions}>{participantStatusesAccordions()}</div>
                <Button
                    id="updateParticipantStatusButton"
                    content="UPDATE PARTICIPANT STATUS"
                    disabled={isUpdateButtonDisabled}
                    error={errors.button}
                    clearError={() => clearError('button')}
                />
            </form>
        </div>
    );
};

ParticipantStatusAdmin.propTypes = {
    contractId: PropTypes.string
};

export default ParticipantStatusAdmin;
